import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import { User } from "../interfaces/user";
import { getCafeStatus } from "../api/cafe.api";
import { getLocalStorage, setLocalStorage, setDocsCookie } from "../api/axios";
import { loginByAccount } from "../api/auth.api";
import { getUserMe } from "../api/user.api";
import { useTheme } from "../theme/useTheme";
import { HalloweenArt } from "../theme/HalloweenArt";
import wordmark from "../assets/images/woxa-cafe-wordmark.png";

export const getUserFromLocalStorage = (): User => {
  const userJson = localStorage.getItem("user");
  const userNull = {
    login: false,
    id: 0,
    sub: "null",
    email: "null",
    name: "null",
    role: "null",
    accessToken: "null",
    isBarOpen: false,
    isCafeOpen: false,
  };
  if (userJson) return JSON.parse(userJson);
  return userNull;
};

const Login = () => {
  const [theme] = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [peek, setPeek] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  /** Where to land after login. Barista has their own work screen; everyone else
   *  starts on the queue. Honour any "from" path the auth wall remembered first. */
  const landingFor = (role?: string) => {
    if (location.state?.from?.pathname) return location.state.from.pathname as string;
    return role === "barista" ? "/ordering" : "/statusOrder";
  };

  const storeRudux = (response: User) => {
    dispatch({ type: "LOGIN", payload: response });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMsg(null);
    if (!username.endsWith("@woxacorp.com")) {
      setErrorMsg("Email must end with @woxacorp.com");
      return;
    }
    setIsLoading(true);
    try {
      const tokens = await loginByAccount({ email: username, password });
      setLocalStorage("access_token", tokens.accessToken);
      setLocalStorage("refresh_token", tokens.refreshToken);
      setDocsCookie(tokens.accessToken);

      const get_token = getLocalStorage("access_token");
      const user = await getUserMe({ access_token: get_token.now_token ?? "" });
      const cafeStatus = await getCafeStatus();
      const responsepost: User = {
        login: true,
        sub: "",
        id: user?.id,
        email: user?.email,
        name: user?.name,
        role: user?.role,
        isBarOpen: cafeStatus?.isBarOpen,
        isCafeOpen: cafeStatus?.isCafeOpen,
      };
      storeRudux(responsepost);
      navigate(landingFor(user?.role), { replace: true });
    } catch (err: any) {
      const status = err?.response?.status as number | undefined;
      const serverMsg = err?.response?.data?.message as string | undefined;
      if (status === 401) {
        setErrorMsg("Email or password is incorrect.");
      } else if (status === 429) {
        setErrorMsg("Too many attempts. Please wait a minute and try again.");
      } else if (status === 403) {
        setErrorMsg(serverMsg ?? "Your account is disabled. Contact admin.");
      } else if (status && status >= 500) {
        setErrorMsg("Server is having a moment. Please try again shortly.");
      } else if (err?.code === "ERR_NETWORK" || err?.message === "Network Error") {
        setErrorMsg("Can't reach the server. Check your internet connection.");
      } else {
        setErrorMsg(serverMsg ?? "Login failed. Please try again.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (localStorage.getItem("access_token")) navigate("/statusOrder");
  }, [navigate]);

  return (
    <div className="cafe-login min-h-screen bg-white font-sans relative overflow-hidden">
      {/* === HERO PANEL — black coffee illustration on bone bg === */}
      <div className="login-hero relative bg-hero text-hero-on px-5 pt-[44px] pb-[80px] overflow-hidden rounded-b-[40px]">
        <HalloweenArt variant="cobweb" className="login-web" />
        {/* Decorative dot grid */}
        <svg
          aria-hidden
          className="absolute inset-0 w-full h-full opacity-[0.07] pointer-events-none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <defs>
            <pattern id="dotgrid" x="0" y="0" width="22" height="22" patternUnits="userSpaceOnUse">
              <circle cx="1.5" cy="1.5" r="1.5" fill="white" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#dotgrid)" />
        </svg>

        {/* Floating coffee marks */}
        <svg aria-hidden className="absolute top-6 right-6 w-[120px] h-[120px] opacity-10" viewBox="0 0 120 120" fill="none">
          <circle cx="60" cy="60" r="58" stroke="white" strokeWidth="1" strokeDasharray="2 4"/>
          <circle cx="60" cy="60" r="40" stroke="white" strokeWidth="1"/>
          <path d="M40 50h35a10 10 0 0 1 0 20h-3M44 70h26v6a8 8 0 0 1-8 8H52a8 8 0 0 1-8-8V50z" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>

        <div className="relative">
          {/* Wordmark */}
          <div className="mb-[64px]">
            <img
              src={wordmark}
              alt="Woxa Cafe"
              className="h-[44px] w-auto"
              style={{ filter: "invert(1)" }}
            />
          </div>

          <p className="login-season">Pumpkin season · Woxa Cafe</p>
          {/* Headline */}
          <p className="eyebrow-v2 text-hero-muted mb-3">Welcome back</p>
          <h1 className="font-display font-black text-[40px] leading-[1.05] tracking-tightest">
            {theme === "halloween" ? <>A little boo.<br/>A lovely brew.</> : <>A great day<br/>for coffee.</>}
          </h1>
          <p className="text-base text-hero-muted mt-3 max-w-[280px]">
            Sign in to order ahead, skip the queue, and track your cup.
          </p>
          <div className="login-companions" aria-hidden="true"><HalloweenArt variant="ghost-coffee" /><HalloweenArt variant="pumpkin" /><HalloweenArt variant="bats" /></div>
        </div>
      </div>

      {/* === FORM CARD — overlaps hero === */}
      <div className="login-form px-5 -mt-[48px] relative z-10">
        <div className="bg-white rounded-3xl border border-ink-100 shadow-floaty px-6 pt-[28px] pb-[24px] max-w-md mx-auto">
          {theme === "halloween" && <div className="login-welcome"><p className="eyebrow-v2">Your café corner</p><h2>Welcome back, coffee friend.</h2><p>เข้าสู่ระบบ แล้วพบกับแก้วโปรดของคุณ</p></div>}
          <form onSubmit={handleSubmit} className="space-y-[6px]">
            <div className="field-v2">
              <input
                id="email"
                type="email"
                autoComplete="email"
                required
                placeholder=" "
                value={username}
                onChange={(e) => setUsername(e.target.value.replace(/\s/g, ""))}
                onKeyDown={(e) => e.key === " " && e.preventDefault()}
              />
              <label htmlFor="email">Email</label>
            </div>

            <div className="field-v2 pr-[40px]">
              <input
                id="password"
                type={peek ? "text" : "password"}
                autoComplete="current-password"
                required
                placeholder=" "
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              <label htmlFor="password">Password</label>
              <button
                type="button"
                onClick={() => setPeek(!peek)}
                className="absolute right-0 bottom-1 h-9 w-9 rounded-full flex items-center justify-center text-ink-400 hover:text-ink-1000 transition"
                aria-label={peek ? "Hide password" : "Show password"}
              >
                {peek ? (
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65"/></svg>
                ) : (
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z"/><path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/></svg>
                )}
              </button>
            </div>

            {errorMsg && (
              <div className="pt-3 text-sm text-ink-1000 font-medium flex items-center gap-2">
                <svg className="w-[16px] h-[16px] flex-shrink-0" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd"/></svg>
                {errorMsg}
              </div>
            )}

            <button
              type="submit"
              disabled={isLoading || !username || !password}
              className="btn-v2-primary btn-v2-lg w-full mt-[28px]"
            >
              {isLoading ? (
                <>
                  <svg className="w-[16px] h-[16px] animate-spin" fill="none" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" strokeOpacity="0.25"/><path d="M12 2a10 10 0 0110 10" stroke="currentColor" strokeWidth="3" strokeLinecap="round"/></svg>
                  Signing in
                </>
              ) : (
                <>
                  Sign in
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M14 5l7 7m0 0l-7 7m7-7H3"/>
                  </svg>
                </>
              )}
            </button>
          </form>
        </div>
      </div>

      {/* === FEATURES — fill the empty middle === */}
      <div className="login-features px-5 pt-[40px] pb-[28px] max-w-md mx-auto">
        <p className="eyebrow-v2 text-center mb-5">Why Woxa Cafe</p>
        <div className="grid grid-cols-3 gap-3">
          {[
            {
              label: "Order ahead",
              icon: (
                <path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z"/>
              ),
            },
            {
              label: "Track queue",
              icon: (
                <>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3"/>
                  <circle cx="12" cy="12" r="9"/>
                </>
              ),
            },
            {
              label: "No queue",
              icon: (
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7"/>
              ),
            },
          ].map((f) => (
            <div key={f.label} className="text-center">
              <div className="w-[44px] h-[44px] rounded-2xl bg-ink-100 flex items-center justify-center mx-auto mb-2">
                <svg className="w-5 h-5 text-ink-1000" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  {f.icon}
                </svg>
              </div>
              <p className="text-xs font-semibold text-ink-700">{f.label}</p>
            </div>
          ))}
        </div>
      </div>

      {/* === FOOTER === */}
      <div className="px-5 pb-[40px] max-w-md mx-auto text-center space-y-2 border-t border-ink-100 pt-[24px]">
        <p className="text-[11px] text-ink-400">
          Still stuck? Contact{" "}
          <button
            type="button"
            onClick={() => navigate("/team-credit")}
            className="text-ink-700 font-semibold underline underline-offset-4"
          >
            Dek Due Crew
          </button>
        </p>
        <p className="text-[11px] text-ink-400">
          © {new Date().getFullYear()} Woxa Corp · Brewed with care
        </p>
      </div>
    </div>
  );
};

export default Login;
